import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../lib/cn';

/**
 * Componente de botón con tres principales variantes:
 * - Button (botón básico con variantes default y outline)
 * - ProminentButton (botón destacado con efecto de brillo)
 * - ServiceButton (botón para servicios de autenticación)
 *
 * @example
 * // Botón básico
 * <UnifiedButton buttonType="basic" variant="default">Click me</UnifiedButton>
 *
 * // Botón prominente
 * <UnifiedButton buttonType="prominent" size="lg">Get Started</UnifiedButton>
 *
 * // Botón de servicio
 * <UnifiedButton buttonType="service" icon={<GoogleIcon />}>Continue with Google</UnifiedButton>
 */
const UnifiedButtonVariants = cva(
  'inline-flex items-center justify-center transition-all cursor-pointer font-normal border-[0.0625rem]',
  {
    variants: {
      // Tipo principal de botón
      buttonType: {
        basic: 'text-sm rounded-md transition-colors font-normal',
        prominent:
          'relative overflow-hidden rounded-full bg-primary-background text-primary-color border',
        service:
          'justify-between rounded-[0.5rem] bg-prominent text-light-secondary border w-full min-w-0',
      },

      // Variantes de estilo (principalmente para tipo 'basic')
      variant: {
        default: `
          border border-transparent
          hover:text-primary-active
          active:border-primary-active active:text-primary-active
          text-primary
        `,
        outline: `
          border border-outline
          hover:border-outline hover:text-primary-active
          active:border-primary-active active:text-primary-active
          text-primary
        `,
      },

      // Tamaños
      size: {
        // Tamaños para botón básico
        default: 'h-[2.25rem] px-3 gap-2',

        // Tamaños para botón prominente
        'prominent-lg': 'h-[3.5rem] max-w-[25rem] w-full px-2 font-bold',
        'prominent-sm': 'h-[2.5rem] max-w-[16.3125rem] w-full px-2',

        // Tamaño para botón de servicio
        service: 'h-[2.25rem] p-[0.625rem]',
      },
      // Tipo de acción (para estilos específicos en botones básicos)
      actionType: {
        default: '',
        submit:
          'bg-primary-active text-background hover:text-background hover:bg-primary-active-hover active:border-transparent active:bg-primary-active-hover-color active:text-background',
        destructive:
          'bg-negative text-light hover:bg-negative/80 active:border-transparent active:bg-negative/60',
      },

      // Estado deshabilitado
      disabledState: {
        true: 'bg-transparent cursor-not-allowed opacity-50 text-primary hover:bg-transparent hover:text-primary',
        false: '',
      },
    },

    compoundVariants: [
      // Variantes compuestas para botón básico
      {
        buttonType: 'basic',
        size: 'default',
      },

      // Efecto de brillo para botón prominente tamaño grande
      {
        buttonType: 'prominent',
        size: 'prominent-lg',
        class: `
          border-primary-active-border text-primary-active hover:border-[0.1rem]
          
          before:absolute
          before:top-1/2
          before:left-[-3.75rem]
          before:w-[1.25rem]
          before:h-[5rem]
          before:bg-white
          before:opacity-100
          before:-translate-y-1/2
          before:rotate-[30deg]
          before:transition-transform
          before:duration-650

          hover:before:translate-x-[32.5rem]
        `,
      },

      // Efecto de brillo para botón prominente tamaño pequeño
      {
        buttonType: 'prominent',
        size: 'prominent-sm',
        class: `
          border-primary-active-border text-primary-active hover:border-[0.15625rem]
          
          before:absolute
          before:top-1/2
          before:left-[-3.75rem]
          before:w-[1.25rem]
          before:h-[5rem]
          before:bg-white
          before:-translate-y-1/2
          before:rotate-[30deg]
          before:transition-transform
          before:duration-650

          hover:before:translate-x-[22.5rem]
        `,
      },
      {
        buttonType: 'service',
        class:
          'border-primary-border hover:border-[0.15625rem] text-light-secondary hover:text-light-secondary',
      },

      // Estilo para botón de servicio
      {
        buttonType: 'service',
        size: 'service',
        class: 'border-white hover:border-[0.15625rem] disabled:cursor-not-allowed',
      },
    ],

    defaultVariants: {
      buttonType: 'basic',
      variant: 'default',
      size: 'default',
      actionType: 'default',
      disabledState: false,
    },
  },
);

export interface UnifiedButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof UnifiedButtonVariants> {
  /**
   * Si es true, el componente se renderiza como un Slot (útil para composición con Radix UI)
   */
  asChild?: boolean;

  /**
   * Ícono a mostrar (usado en buttonType 'basic' y 'service')
   * - En 'basic': se muestra antes del texto
   * - En 'service': se muestra al inicio del botón
   */
  icon?: React.ReactNode;

  /**
   * Alias para compatibilidad con el componente Button original
   * @deprecated Use 'icon' instead
   */
  iconImg?: React.ReactNode;

  /**
   * Si es true, muestra un spinner y deshabilita el botón
   * Useful para indicar estado de carga durante operaciones async
   */
  loading?: boolean;

  /**
   * Texto personalizado a mostrar durante el estado de loading
   * Si no se especifica, muestra el children original
   */
  loadingText?: string;
}

const UnifiedButton = React.forwardRef<HTMLButtonElement, UnifiedButtonProps>(
  (
    {
      className,
      buttonType = 'basic',
      variant,
      size,
      actionType,
      asChild = false,
      icon,
      iconImg,
      children,
      disabled,
      loading = false,
      loadingText,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';

    // Usar iconImg si está presente (retrocompatibilidad)
    const displayIcon = icon || iconImg;
    const hasIcon = Boolean(displayIcon);

    // Determinar el tamaño apropiado según el tipo de botón
    const computedSize =
      size ||
      (buttonType === 'prominent'
        ? 'prominent-lg'
        : buttonType === 'service'
          ? 'service'
          : 'default');

    // Determinar si el botón está deshabilitado
    const isDisabled = disabled || loading;

    // Texto a mostrar
    const displayText = loading && loadingText ? loadingText : children;

    // Renderizar según el tipo de botón
    if (buttonType === 'service') {
      return (
        <Comp
          ref={ref}
          disabled={isDisabled}
          className={cn(
            UnifiedButtonVariants({
              buttonType,
              size: computedSize,
              disabledState: isDisabled,
              className,
            }),
          )}
          {...props}
        >
          <span>{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : displayIcon}</span>
          <span className="flex items-center gap-2 min-w-0">{displayText}</span>
          <span></span>
        </Comp>
      );
    }

    // Para 'basic' y 'prominent'
    return (
      <Comp
        ref={ref}
        disabled={isDisabled}
        className={cn(
          UnifiedButtonVariants({
            buttonType,
            variant: buttonType === 'basic' ? variant : undefined,
            size: computedSize,
            disabledState: isDisabled,
            className,
            actionType,
          }),
        )}
        {...props}
      >
        {loading && buttonType === 'basic' ? (
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
        ) : (
          hasIcon &&
          buttonType === 'basic' && <span className="flex items-center">{displayIcon}</span>
        )}
        {displayText}
      </Comp>
    );
  },
);

UnifiedButton.displayName = 'UnifiedButton';

export { UnifiedButton, UnifiedButtonVariants };
